import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Form,
  Row,
  Col,
  Card,
  Button,
  Table,
  ButtonGroup,
  Spinner
} from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import PageHeader from 'app/components/header/PageHeader';
import cloudUpload from 'assets/img/icons/cloud-upload.svg';
import Flex from 'components/common/Flex';
import useRequest from 'app/hooks/useRequest';
import { dropdown } from 'app/api/TaskPriceService';
import { dropdown as impression } from 'app/api/costImpression';
import { upload } from 'app/api/UploadsService';
import UserContext from 'app/context/UserContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { create, one, update } from 'app/api/TaskService';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';

function AddTask() {
  const [businessName, setBusinessName] = useState('');
  const [noOfLeads, setNoOfLeads] = useState('');
  const [taskLocation,setTaskLocation] = useState('')
  const [taskPricings, setTaskPricings] = useState([]);
  const [taskPricingOptions, setTaskPricingOptions] = useState([]);
  const [backgroundImagePath, setBackgroundImagePath] = useState('');
  const [logoImagePath, setLogoImagePath] = useState('');
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const [impressionData, setImpressionData] = useState([]);

  const { id } = useParams();
  const { response, sendData } = useRequest();
  const { user } = useContext(UserContext);

  const uploadImage = async (file, setPathCallback) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await upload(user.token, formData);
      if (res.data?.relativePath) {
        setPathCallback(res.data.relativePath);
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Upload failed: No relativePath in response');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = setPathCallback =>
    useCallback(acceptedFiles => {
      if (acceptedFiles.length > 0) {
        uploadImage(acceptedFiles[0], setPathCallback);
      }
    }, []);

  const {
    getRootProps: getRootPropsBackground,
    getInputProps: getInputPropsBackground
  } = useDropzone({
    onDrop: handleDrop(setBackgroundImagePath),
    accept: 'image/*',
    multiple: false
  });

  const { getRootProps: getRootPropsLogo, getInputProps: getInputPropsLogo } =
    useDropzone({
      onDrop: handleDrop(setLogoImagePath),
      accept: 'image/*',
      multiple: false
    });

  const handleTaskPricingChange = id => {
    setTaskPricings(prev => {
      const exists = prev.some(item => item.id === id);
      const updated = exists
        ? prev.filter(item => item.id !== id)
        : [...prev, { id }];

      // If unchecking (i.e. removing), clear the input manually
      if (exists) {
        const input = document.getElementById(`url-input-${id}`);
        if (input) input.value = '';
      }

      return updated;
    });
  };

  const handleSubmit = async () => {
    const payload = {
      businessName,
      taskLocation,
      noOfLeads: Number(noOfLeads),
      logoImage: logoImagePath,
      backgroundImage: backgroundImagePath,
      taskPricings
    };

    setUploading(true);

    try {
      const config = id
        ? update(user.token, id, payload)
        : create(user.token, payload);

      const response = await axios.request(config);

      if (response.data.success) {
        toast.success(
          response.data.message ||
            (id ? 'Task updated successfully' : 'Task created successfully'),
          { theme: 'colored' }
        );

        if (!id) {
          setBusinessName('');
          setNoOfLeads('');
          setLogoImagePath('');
          setBackgroundImagePath('');
          setTaskLocation('')
          setTaskPricings([]);
        }
        navigate('/dashboard/task');
      } else {
        toast.error(response.data.message || 'Operation failed');
      }
    } catch (error) {
      console.error(error);
      toast.error(
        id
          ? 'An error occurred during task update'
          : 'An error occurred during task creation',
        { theme: 'colored' }
      );
    } finally {
      setUploading(false);
    }
  };

  const getTaskPricing = () => {
    const config = dropdown(user.token);
    sendData(config);
  };

  useEffect(() => {
    if (response?.data?.data) {
      setTaskPricingOptions(response.data.data);
    }
  }, [response]);

  useEffect(() => {
    getTaskPricing();
    getImpressionData()
  }, []);
  

  const getTaskDetail = id => {
    const config = one(user.token, id);

    axios
      .request(config)
      .then(res => {
        const data = res.data?.data;

        if (data) {
          setBusinessName(data.businessName || '');
          setNoOfLeads(data.noOfLeads || '');
          setLogoImagePath(data.logoImage || '');
          setBackgroundImagePath(data.backgroundImage || '');
          setTaskLocation(data.taskLocation || '');

          const pricingIds =
          data.assignedPrices?.map(p => ({
            id: p.taskPricingId,
            relativePath: p.relativePath,
            fileType: p.fileType,
            formatType: p.formatType,
            task_description: p.task_description || "",
            ...(p.impressionId && { impressionId: p.impressionId })
          })) || [];

          setTaskPricings(pricingIds);
        }
      })
      .catch(err => {
        console.error('Error fetching task detail:', err);
        toast.error('Failed to fetch task details');
      });
  };

  async function UploadFile(file, id,isTaskType) {
    setUploading(true); // Start uploading state

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await upload(user.token, formData);
      const { fileType, formatType, relativePath } = res.data || {};

      if (relativePath) {
        toast.success('File uploaded successfully');

        // Update taskPricings state
        setTaskPricings(prev =>
          prev.map(item =>
            item.id === id
              ? {
                  ...item,
                  fileType,
                  formatType,
                  relativePath,
                  isTaskType: isTaskType
                }
              : item
          )
        );
      } else {
        toast.error('Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('File upload failed');
    } finally {
      setUploading(false); // End uploading state
    }
  }

 function addImpressionToTask(id, impressionId,isTaskType) {
  setTaskPricings(prev =>
    prev.map(item =>
      item.id === id
        ? {
            ...item,
            impressionId: Number(impressionId),
            isTaskType: isTaskType
          }
        : item
    )
  );
}


  function handleUrlChange(url, id, isTaskType) {
    setTaskPricings(prev =>
      prev.map(item =>
        item.id === id
          ? {
              ...item,
              relativePath: url,
              fileType: null,
              formatType: null,
              isTaskType: isTaskType
            }
          : item
      )
    );
  }

  function handleDescChange(des, id) {
    setTaskPricings(prev =>
      prev.map(item =>
        item.id === id
          ? {
              ...item,
              task_description: des
            }
          : item
      )
    );
  }


  async function getImpressionData() {
    try {
      const response = await axios.request(impression(user.token));

      if (response.data.success) {
        setImpressionData(response.data.data);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching impressions:', error);
      toast.error('An error occurred while fetching pricing impressions.');
    }
  }

  useEffect(() => {
    if (id) {
      getTaskDetail(id);
    }
  }, [id]);

  const isFormValid =
    businessName.trim() !== '' &&
    taskLocation.trim() !== '' &&
    Number(noOfLeads) > 0 &&
    backgroundImagePath !== '' &&
    logoImagePath !== '' &&
    taskPricings.length > 0 &&
    taskPricings.every(tp => Object.keys(tp).length > 1) &&
    !uploading;

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`Task | ${process.env.REACT_APP_PROJECT_NAME}`}</title>
      </Helmet>

      <PageHeader
        col={{ xs: 6 }}
        title={id ? (
                'Update Task'
              ) : (
                'Add Task'
              )}
        className="mb-3"
      />

      <Card className="mb-3">
        <Card.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Business Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={businessName}
                    onChange={e => setBusinessName(e.target.value)}
                    placeholder="Enter business name"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>No. of Leads</Form.Label>
                  <Form.Control
                    type="number"
                    disabled={id}
                    value={noOfLeads}
                    onChange={e => setNoOfLeads(e.target.value)}
                    placeholder="Enter number of leads"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Background Image</Form.Label>
                  <div
                    {...getRootPropsBackground({
                      className: 'dropzone-area py-6'
                    })}
                  >
                    <input {...getInputPropsBackground()} />
                    <Flex justifyContent="center">
                      <img
                        src={cloudUpload}
                        alt=""
                        width={25}
                        className="me-2"
                      />
                      <p className="fs-0 mb-0 text-700">
                        Drop background image here
                      </p>
                    </Flex>
                  </div>
                  {backgroundImagePath && (
                    <div className="mt-2 text-info">
                      <img
                        src={backgroundImagePath}
                        alt="Background Preview"
                        width={100}
                        height={100}
                      />
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Logo</Form.Label>
                  <div
                    {...getRootPropsLogo({ className: 'dropzone-area py-6' })}
                  >
                    <input {...getInputPropsLogo()} />
                    <Flex justifyContent="center">
                      <img
                        src={cloudUpload}
                        alt=""
                        width={25}
                        className="me-2"
                      />
                      <p className="fs-0 mb-0 text-700">Drop logo here</p>
                    </Flex>
                  </div>
                  {logoImagePath && (
                    <div className="mt-2 text-info">
                      <img
                        src={logoImagePath}
                        alt="Logo Preview"
                        width={100}
                        height={100}
                      />
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Task Location</Form.Label>
                  <Form.Control
                    type="text"
                    value={taskLocation}
                    onChange={e => setTaskLocation(e.target.value)}
                    placeholder="Enter task Location"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Task Pricings</Form.Label>
                  {taskPricingOptions.length > 0 ? (
                    <Table bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Select</th>
                          <th>Title</th>
                          <th>Task Type</th>
                          <th>Action</th>
                          <th>Pricing Impression</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taskPricingOptions.map(pricing => (
                          <tr key={pricing.id}
                          style={id
                            ? { pointerEvents: 'none', opacity: 0.5 }
                            : {}}
                          >
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={taskPricings.some(
                                  tp => tp.id === pricing.id
                                )}
                                onChange={() =>
                                  handleTaskPricingChange(pricing.id)
                                }
                              />
                            </td>
                            <td>{pricing.title}</td>
                            <td>
                              {pricing.isTaskType ? 'Task' : 'Impression'}
                            </td>
                            <td>
                              {pricing.type == 'url' ? (
                                <input
                                  type="text"
                                  disabled={
                                    !taskPricings.some(
                                      tp => tp.id === pricing.id
                                    )
                                  }
                                  value={
                                    taskPricings.find(
                                      tp => tp.id === pricing.id
                                    )?.relativePath
                                  }
                                  placeholder="Enter URL"
                                  className="form-control"
                                  id={`url-input-${pricing.id}`}
                                  onChange={e =>
                                    handleUrlChange(e.target.value, pricing.id,pricing.isTaskType)
                                  }
                                />
                              ) : (
                                <>
                                  <input
                                    type="file"
                                    id={`file-input-${pricing.id}`}
                                    className="d-none"
                                    accept={!pricing.isTaskType ? 'image/*,video/*' : undefined}
                                    onChange={e => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        UploadFile(file, pricing.id,pricing.isTaskType);
                                      }
                                    }}
                                  />
                                  <>
                                    <Button
                                      disabled={
                                        !taskPricings.some(
                                          tp => tp.id === pricing.id
                                        )
                                      }
                                      variant="primary"
                                      size="sm"
                                      onClick={() =>
                                        document
                                          .getElementById(
                                            `file-input-${pricing.id}`
                                          )
                                          .click()
                                      }
                                    >
                                      Upload
                                    </Button>
                                    {pricing.type == 'file' &&
                                      (() => {
                                        const match = taskPricings.find(
                                          tp => tp.id === pricing.id
                                        );
                                        return match?.relativePath ? (
                                          <Link
                                            to={match.relativePath}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary ms-2"
                                          >
                                            View
                                          </Link>
                                        ) : null;
                                      })()}
                                  </>
                                </>
                              )}
                            </td>
                            <td>
                              {!pricing.isTaskType && (
                                <Form.Group className="mb-3">
                                  <Form.Select
                                  disabled={ !taskPricings.some(
                                          tp => tp.id === pricing.id
                                        )}
                                    value={
                                        taskPricings.find(tp => tp.id === pricing.id)?.impressionId || ''
                                      }
                                    onChange={e =>
                                        addImpressionToTask(pricing.id, e.target.value, pricing.isTaskType)
                                      }
                                  >
                                    <option value="">
                                      Select an impression
                                    </option>
                                    {impressionData?.length > 0 ? (
                                      impressionData.map(value => (
                                        <option key={value.id} value={value.id}>
                                          No Of Cost ({value.noOfCost}) -
                                          Impressions ({value.impressions})
                                        </option>
                                      ))
                                    ) : (
                                      <option disabled>
                                        Loading impressions...
                                      </option>
                                    )}
                                  </Form.Select>
                                </Form.Group>
                              )}
                            </td>
                            <td>
                              <textarea
                                  disabled={
                                    !taskPricings.some(
                                      tp => tp.id === pricing.id
                                    )
                                  }
                                  value={
                                    taskPricings.find(
                                      tp => tp.id === pricing.id
                                    )?.task_description
                                  }
                                name="task_description"
                                id="task_description"
                                className='form-control'
                                rows={1}
                                placeholder="Enter description"
                                onChange={(e)=> handleDescChange(e.target.value,pricing.id)}
                              ></textarea>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-muted">Loading...</div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Button
              variant="success"
              onClick={handleSubmit}
              disabled={!isFormValid}
            >
              {uploading ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Uploading...
                </>
              ) : id ? (
                'Update Task'
              ) : (
                'Submit Task'
              )}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
}

export default AddTask;
